import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ErrorService } from '../shared/services/error.service';
import { buildFinancialApiUrl } from '../utils/financial-api-url.util';
import {
  VehicleIpvaLicensingPaymentHistory,
  VehicleIpvaLicensingBankProvider,
  VehicleIpvaLicensingRequestPaymentPayload,
  VehicleIpvaLicensingRequestPaymentResponse,
  VehicleIpvaLicensingRecordManualPayload,
  VehicleIpvaLicensingRefreshStatusResponse
} from '../models/VehicleIpvaLicensing';

const PAYMENT_TYPES = ['IPVA', 'Licenciamento'] as const;

@Injectable({ providedIn: 'root' })
export class VehicleIpvaLicensingService {

  private financialUrl(path = ''): string {
    return buildFinancialApiUrl('vehicle-ipva-licensing', path);
  }

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  private mapHistory(dto: any): VehicleIpvaLicensingPaymentHistory {
    return {
      id: dto?.id ?? 0,
      vehicleId: dto?.vehicleId ?? 0,
      paymentType: PAYMENT_TYPES.includes(dto?.paymentType) ? dto.paymentType : 'IPVA',
      referenceYear: dto?.referenceYear ?? 0,
      paymentDate: dto?.paymentDate ?? null,
      amount: dto?.amount ?? null,
      status: dto?.status ?? 'Pendente',
      documentNumber: dto?.documentNumber ?? null,
      externalReference: dto?.externalReference ?? null,
      notes: dto?.notes ?? null
    };
  }

  getBankProvider(): Observable<VehicleIpvaLicensingBankProvider> {
    return this.http.get<any>(this.financialUrl('/bank-provider')).pipe(
      map(res => ({
        isAvailable: res?.isAvailable === true,
        providerName: res?.providerName ?? 'Nenhum (stub)'
      })),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao verificar provedor bancário');
        return throwError(() => err);
      })
    );
  }

  getByVehicle(vehicleId: number): Observable<VehicleIpvaLicensingPaymentHistory[]> {
    return this.http.get<any[]>(this.financialUrl(`/vehicle/${vehicleId}`)).pipe(
      map(list => (Array.isArray(list) ? list : []).map(dto => this.mapHistory(dto))),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao carregar histórico');
        return throwError(() => err);
      })
    );
  }

  getByVehicleAndType(vehicleId: number, paymentType: 'IPVA' | 'Licenciamento'): Observable<VehicleIpvaLicensingPaymentHistory[]> {
    return this.http.get<any[]>(this.financialUrl(`/vehicle/${vehicleId}/type/${encodeURIComponent(paymentType)}`)).pipe(
      map(list => (Array.isArray(list) ? list : []).map(dto => this.mapHistory(dto))),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao carregar histórico');
        return throwError(() => err);
      })
    );
  }

  getByVehicleAndYear(vehicleId: number, referenceYear: number): Observable<VehicleIpvaLicensingPaymentHistory[]> {
    return this.http.get<any[]>(this.financialUrl(`/vehicle/${vehicleId}/year/${referenceYear}`)).pipe(
      map(list => (Array.isArray(list) ? list : []).map(dto => this.mapHistory(dto))),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao carregar histórico');
        return throwError(() => err);
      })
    );
  }

  getById(id: number): Observable<VehicleIpvaLicensingPaymentHistory> {
    return this.http.get<any>(this.financialUrl(`/${id}`)).pipe(
      map(dto => this.mapHistory(dto)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao carregar registro');
        return throwError(() => err);
      })
    );
  }

  requestPayment(payload: VehicleIpvaLicensingRequestPaymentPayload): Observable<VehicleIpvaLicensingRequestPaymentResponse> {
    return this.http.post<any>(this.financialUrl('/request-payment'), payload).pipe(
      map(res => ({
        success: res?.success === true,
        message: res?.message ?? '',
        history: res?.history ? this.mapHistory(res.history) : undefined,
        boletoUrl: res?.boletoUrl
      })),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao solicitar pagamento');
        return throwError(() => err);
      })
    );
  }

  recordManual(payload: VehicleIpvaLicensingRecordManualPayload): Observable<VehicleIpvaLicensingPaymentHistory> {
    return this.http.post<any>(this.financialUrl('/record-manual'), payload).pipe(
      map(dto => this.mapHistory(dto)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao registrar pagamento');
        return throwError(() => err);
      })
    );
  }

  refreshStatus(id: number): Observable<VehicleIpvaLicensingRefreshStatusResponse> {
    return this.http.post<any>(this.financialUrl(`/${id}/refresh-status`), {}).pipe(
      map(res => ({
        updated: res?.updated === true,
        message: res?.message ?? null
      })),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao atualizar status');
        return throwError(() => err);
      })
    );
  }
}
